import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Box, Heading, Flex, Text, Badge, useToast } from "@chakra-ui/react";
import TaskCard from "./TaskCard";
import { api } from "../../lib/api";

const COLUMNS = [
  { key: "todo", label: "To do" },
  { key: "in_progress", label: "In Progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];

export default function KanbanBoard({ tasks, onTaskClick, onChange }) {
  const toast = useToast();

  const grouped = COLUMNS.reduce((acc, c) => ((acc[c.key] = []), acc), {});
  (tasks || []).forEach((t) => {
    const k = grouped[t.status] ? t.status : "todo";
    grouped[k].push(t);
  });

  async function handleDragEnd(result) {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newStatus = destination.droppableId;
    const previousTasks = tasks;
    const updatedTasks = tasks.map((t) => (t._id === draggableId ? { ...t, status: newStatus } : t));
    if (onChange) onChange(updatedTasks);

    try {
      await api(`/tasks/${draggableId}/move`, {
        method: "POST",
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (e) {
      toast({ title: e.message, status: "error" });
      if (onChange) onChange(previousTasks);
    }
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Flex gap={4} overflowX="auto" pb={2}>
        {COLUMNS.map((col) => (
          <Box key={col.key} minW="280px" w="280px" bg="bg.subtle" rounded="lg" p={3} borderWidth="1px" borderColor="border.subtle">
            <Flex align="center" justify="space-between" mb={3}>
              <Heading size="xs" textTransform="uppercase" color="text.tertiary" letterSpacing="0.06em" fontWeight={600}>{col.label}</Heading>
              <Box fontSize="xs" color="text.tertiary" px={1.5} py={0.5} bg="bg.surface" rounded="md" fontWeight={500}>{grouped[col.key].length}</Box>
            </Flex>
            <Droppable droppableId={col.key}>
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  minH="100px"
                  bg={snapshot.isDraggingOver ? "accent.soft" : "transparent"}
                  rounded="md"
                  transition="background 160ms cubic-bezier(0.4,0,0.2,1)"
                >
                  {grouped[col.key].map((t, idx) => (
                    <Draggable key={t._id} draggableId={t._id} index={idx}>
                      {(prov, snap) => (
                        <Box
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          {...prov.dragHandleProps}
                          mb={2}
                        >
                          <TaskCard task={t} dragging={snap.isDragging} onClick={() => onTaskClick && onTaskClick(t)} />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {grouped[col.key].length === 0 && !snapshot.isDraggingOver && (
                    <Text fontSize="xs" color="text.tertiary" textAlign="center" py={4}>Drop tasks here</Text>
                  )}
                </Box>
              )}
            </Droppable>
          </Box>
        ))}
      </Flex>
    </DragDropContext>
  );
}
